// ============================================================================
// Hiring KPI tiles data layer (Antonela spec §3.1 — Tiles A/B/C).
//
// One small fetch of hiring_requests (status, created_date, closed_date) drives
// all three tiles + their trend arrows, computed client-side:
//   A. Open Hiring Requests — status IN (Open, Interview, On_Hold).
//      Trend vs ~7 days ago (reconstructed from created/closed dates).
//   B. Positions Filled This Month — status='Filled', closed_date in this
//      calendar month. Trend vs last calendar month.
//   C. Avg Days to Fill — avg(closed_date − created_date) for Filled rows
//      closed in the last 30 days. Trend vs the prior 30-day window.
//
// Trend caveat (documented in the session report): "open ~7 days ago" is a
// reconstruction from existence + closure dates, not a historical snapshot —
// status-as-of-then isn't recorded. It's directionally correct for the arrow.
// ============================================================================

import { useEffect, useState } from 'react';
import { supabase } from '../../../shared/supabase';
import { HIRING_OPEN_STAGES } from '../../../shared/types';
import { errorMessage } from '../../../shared/errors';

export interface HiringKpis {
  openCount: number;
  openDelta: number | null; // vs ~7 days ago
  filledThisMonth: number;
  filledDelta: number | null; // vs last calendar month
  avgDaysToFill: number | null; // last 30 days
  avgDaysDelta: number | null; // vs prior 30-day window (negative = faster)
}

interface State {
  data: HiringKpis | null;
  isLoading: boolean;
  error: string | null;
  refetch: () => void;
}

interface Row {
  status: string | null;
  created_date: string | null;
  closed_date: string | null;
}

const DAY = 86_400_000;

export function useHiringKpis(enabled: boolean): State {
  const [data, setData] = useState<HiringKpis | null>(null);
  const [isLoading, setIsLoading] = useState(enabled);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    if (!enabled) {
      setData(null);
      setIsLoading(false);
      return;
    }
    let cancelled = false;
    setIsLoading(true);
    setError(null);
    (async () => {
      try {
        const { data: rows, error: e } = await supabase
          .from('hiring_requests')
          .select('status, created_date, closed_date');
        if (e) throw e;
        if (cancelled) return;
        setData(compute((rows ?? []) as Row[]));
      } catch (err) {
        if (cancelled) return;
        console.error('[hiring-kpis] load failed', err);
        setError(errorMessage(err, 'Failed to load hiring KPIs.'));
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [enabled, tick]);

  return { data, isLoading, error, refetch: () => setTick((n) => n + 1) };
}

function compute(rows: Row[]): HiringKpis {
  const openStages = new Set<string>(HIRING_OPEN_STAGES);
  const now = Date.now();
  const d = new Date();
  const thisMonthStart = new Date(d.getFullYear(), d.getMonth(), 1).getTime();
  const lastMonthStart = new Date(d.getFullYear(), d.getMonth() - 1, 1).getTime();
  const sevenAgo = now - 7 * DAY;
  const thirtyAgo = now - 30 * DAY;
  const sixtyAgo = now - 60 * DAY;

  // A — open now
  const openCount = rows.filter((r) => openStages.has(r.status ?? 'No_Status')).length;

  // A trend — reconstruct "open ~7 days ago" from existence + closure.
  let openAsOf7 = 0;
  for (const r of rows) {
    const created = parse(r.created_date);
    if (created == null || created > sevenAgo) continue;
    const closed = parse(r.closed_date);
    if (closed != null && closed <= sevenAgo) continue; // already closed by then
    openAsOf7 += 1;
  }
  const openDelta = openCount - openAsOf7;

  // B — filled this month vs last month
  let filledThisMonth = 0;
  let filledLastMonth = 0;
  for (const r of rows) {
    if (r.status !== 'Filled') continue;
    const closed = parse(r.closed_date);
    if (closed == null) continue;
    if (closed >= thisMonthStart) filledThisMonth += 1;
    else if (closed >= lastMonthStart && closed < thisMonthStart) filledLastMonth += 1;
  }
  const filledDelta = filledThisMonth - filledLastMonth;

  // C — avg days to fill, last 30 days vs prior 30 days
  const cur: number[] = [];
  const prev: number[] = [];
  for (const r of rows) {
    if (r.status !== 'Filled') continue;
    const created = parse(r.created_date);
    const closed = parse(r.closed_date);
    if (created == null || closed == null) continue;
    const days = Math.max(0, Math.round((closed - created) / DAY));
    if (closed >= thirtyAgo) cur.push(days);
    else if (closed >= sixtyAgo && closed < thirtyAgo) prev.push(days);
  }
  const avgDaysToFill = cur.length ? Math.round(avg(cur)) : null;
  const avgDaysDelta =
    cur.length && prev.length ? Math.round(avg(cur) - avg(prev)) : null;

  return {
    openCount,
    openDelta,
    filledThisMonth,
    filledDelta,
    avgDaysToFill,
    avgDaysDelta,
  };
}

function parse(v: string | null): number | null {
  if (!v) return null;
  const t = Date.parse(v);
  return Number.isFinite(t) ? t : null;
}

function avg(xs: number[]): number {
  return xs.reduce((s, x) => s + x, 0) / xs.length;
}
